import {
  doseDecision,
  type DoseDecision,
  type RecordedDose,
} from "@src/packages/injection-protocol/dose";
import type {
  InjectionProtocol,
  ProtocolDoseUnit,
} from "@src/packages/injection-protocol/types";

/**
 * Whether the walk may name her dose, and what to ask her if it may not.
 *
 * Pure, like the device and route decisions, so the host component carries no
 * opinion of its own about a number: it renders whatever stage this returns.
 *
 * The three outcomes, in the order a patient meets them:
 *
 * - **settled** — her record agrees with the label, or there is no record, or
 *   she has just told us what her box says. `dose` is what the steps may say,
 *   and `null` means they say nothing.
 * - **ask** — her record names a dose the label does not: the wrong unit, or a
 *   strength the product is not sold in. The question quotes her record back
 *   to her and offers the label's strengths as buttons, because the answer is
 *   printed on the box in her hand and not something she has to know. A product
 *   with no fixed strengths has nothing to offer, so the only way on is without
 *   a dose.
 * - **unguided** — she says her box matches none of the strengths offered.
 *   Nothing here can place that, and the honest answer is a card that says so
 *   and sends her to her clinic and her record, rather than a walk that quietly
 *   names nothing.
 */
export type DoseQuestionStage = "settled" | "ask" | "unguided";

export type DoseQuestionInput = {
  protocol: InjectionProtocol;
  /** Her record's dose as the surface carries it — "150 mg", or nothing. */
  recorded: string | null | undefined;
  /** The strength she picked this session, as the button said it. */
  answered: string | null;
  /** She said her box says none of the strengths offered. */
  unlisted: boolean;
  /** She chose to go on without the steps naming a dose. */
  withoutDose: boolean;
};

export type DoseQuestion = {
  stage: DoseQuestionStage;
  /** What the steps may say. Null means they name no number. */
  dose: string | null;
  /** The label's strengths, formatted, for the buttons. Empty when titrated. */
  options: readonly string[];
  /** Why she is being asked, for the sentence that explains it. */
  mismatch: Extract<DoseDecision, { kind: "mismatch" }> | null;
};

export function doseQuestion({
  protocol,
  recorded,
  answered,
  unlisted,
  withoutDose,
}: DoseQuestionInput): DoseQuestion {
  const decision = doseDecision(protocol, recorded);

  if (decision.kind === "none") {
    return { stage: "settled", dose: null, options: [], mismatch: null };
  }
  if (decision.kind === "matches") {
    return {
      stage: "settled",
      dose: decision.dose,
      options: [],
      mismatch: null,
    };
  }

  const options = decision.strengths;
  if (unlisted) {
    return { stage: "unguided", dose: null, options, mismatch: decision };
  }
  if (withoutDose) {
    return { stage: "settled", dose: null, options, mismatch: decision };
  }
  // Only a strength the label offers can settle it — her answer is one of the
  // buttons, and a button is one of the label's own numbers.
  if (answered && options.includes(answered)) {
    return { stage: "settled", dose: answered, options, mismatch: decision };
  }
  return { stage: "ask", dose: null, options, mismatch: decision };
}

/**
 * The structured value behind a chosen button, for the record correction.
 *
 * Read back from the protocol rather than parsed from the label, so what is
 * written to her record is the label's number in the label's unit and nothing
 * that passed through a string.
 */
export function chosenDoseValue(
  protocol: InjectionProtocol,
  answered: string,
): { value: number; unit: ProtocolDoseUnit } | null {
  if (!protocol.dose) return null;
  const { unit, strengths = [] } = protocol.dose;
  const value = strengths.find(
    (candidate) => `${candidate} ${unit}` === answered,
  );
  return value === undefined ? null : { value, unit };
}

export type { RecordedDose };
